import { useRouter } from "next/router";
import dayjs from "dayjs";
import ErrorPage from "next/error";
import Image from "next/legacy/image";
import Link from "next/link";

import { blurDataURL } from "../../lib/constants";
import { getAllPostsWithSlug, getPostAndMorePosts } from "../../lib/api";
import { imageBuilder } from "../../lib/sanity";
import BlockContent from "../../components/block-content";
import Meta from "../../components/meta";
import MoreStories from "../../components/more-stories";

export default function Post({
  post = {},
  morePosts = [],
  // eslint-disable-next-line no-unused-vars
  preview,
}) {
  const layout = "centered";
  const maxWidthClassName = "max-w-container";
  const containerPaddingClassName = "py-2";
  const containerPaddingXClassName = "px-4 sm:px-6 xl:px-8";
  const {
    title,
    createdAt,
    updatedAt,
    categories = [],
    coverImage: image,
    body,
  } = post || {};

  const category = (categories && categories[0]) || {};
  const categoryHref = `/blog?category=${
    category.slug && category.slug.current
  }`;
  const breadcrumbs = [
    { id: 1, name: "Blog", href: "/blog" },
    {
      id: 2,
      name: category.title,
      href: categoryHref,
    },
  ];

  const router = useRouter();
  if (!router.isFallback && !post?.slug) {
    return <ErrorPage statusCode={404} />;
  }
  return <>
    <Meta
      path={`/blog/${post.slug}`}
      title={post.title}
      description={post.description}
      image={image}
    />

    <article>
      <div className="bg-white overflow-hidden mt-8">
        <div
          className={`relative ${maxWidthClassName} mx-auto ${containerPaddingClassName} ${containerPaddingXClassName} min-h-screen`}
        >
          <div
            className={`${
              layout === "centered"
                ? ""
                : "hidden xl:block bg-gray-50 absolute top-0 bottom-0 left-3/4 w-screen rounded-xl"
            }`}
          />
          <div
            className={`${
              layout === "centered"
                ? ""
                : " xl:grid xl:grid-cols-2 xl:gap-x-8 xl:max-w-none"
            } mx-auto text-base max-w-prose`}
          >
            <nav aria-label="Breadcrumb" className="mb-8 hidden md:block">
              <ol role="list" className="flex items-center space-x-4">
                {breadcrumbs.map((breadcrumb) => (
                  <li key={breadcrumb.id}>
                    <div className="flex items-center">
                      <a
                        href={breadcrumb.href}
                        className="mr-4 text-sm font-medium text-gray-900"
                      >
                        {breadcrumb.name}
                      </a>
                      <svg
                        viewBox="0 0 6 20"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        className="h-5 w-auto text-gray-300"
                      >
                        <path
                          d="M4.878 4.34H3.551L.27 16.532h1.327l3.281-12.19z"
                          fill="currentColor"
                        />
                      </svg>
                    </div>
                  </li>
                ))}
                <li className="text-sm">
                  <Link
                    href={`/blog/${post.slug}`}
                    aria-current="page"
                    className="font-medium text-gray-500 hover:text-gray-600">

                    {title}

                  </Link>
                </li>
              </ol>
            </nav>

            <div>
              <Link
                href={categoryHref}
                className="text-base hover:underline text-accent-500 font-semibold tracking-wide uppercase mb-2 inline-flex">

                {category.title}

              </Link>
              <div className="block text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                {title}
              </div>
            </div>
          </div>

          <div className={`mt-6`}>
            <div className="mb-8 xl:mt-0">
              <div
                className={`${
                  layout === "centered" ? "" : "xl:max-w-none"
                } max-w-prose mx-auto mb-8`}
              >
                <div className="text-xl text-gray-500 leading-8">
                  {post.description}
                </div>
                <div className="font-light text-gray-500 text-opacity-90 text-sm">
                  <div className="mt-4">
                    By {post.author.name} on{" "}
                    {dayjs(createdAt).format("MMM D, YYYY")}
                  </div>
                  {updatedAt !== createdAt ? (
                    <div className="italic">
                      Updated {dayjs(updatedAt).format("MMM D, YYYY")}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="relative">
                <div
                  className={`${
                    layout === "centered" ? "" : "xl:max-w-none"
                  } relative text-base mx-auto max-w-prose`}
                >
                  <div
                    className={`${
                      layout === "centered"
                        ? "aspect-w-16 aspect-h-9 mb-6"
                        : "aspect-w-12 aspect-h-7 xl:aspect-w-37 xl:aspect-h-43"
                    }`}
                  >
                    <Image
                      draggable={false}
                      className="h-full w-full overflow-hidden rounded-lg shadow-lg object-cover object-center"
                      alt=""
                      layout="fill"
                      placeholder="blur"
                      blurDataURL={blurDataURL}
                      src={imageBuilder(image).width(1980).height(1320).url()}
                    />
                  </div>
                </div>
              </div>

              <div
                className={`${
                  layout === "centered" ? "" : "xl:max-w-none"
                } max-w-prose mx-auto`}
              >
                <BlockContent
                  className="text-gray-500 prose"
                  content={body}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>

    {morePosts.length > 0 && <MoreStories posts={morePosts} />}
  </>;
}

export async function getStaticProps({ params, preview = false }) {
  const data = await getPostAndMorePosts(params.slug, preview);
  return {
    props: {
      preview,
      post: data?.post || null,
      morePosts: data?.morePosts || null,
    },
    revalidate: 1,
  };
}

export async function getStaticPaths() {
  const allPosts = await getAllPostsWithSlug();
  return {
    paths:
      allPosts?.map((post) => ({
        params: {
          slug: post.slug,
        },
      })) || [],
    fallback: "blocking",
  };
}
